import { useOmniboxEnrichment } from "@/contexts/OmniboxEnrichmentContext.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { Temporal } from "temporal-polyfill";
import { useEffect } from "react";

const ENRICHMENT_ID = "calendar-selected-event";

/**
 * The bits of a calendar event this enrichment needs. Kept as a flat shape
 * (rather than the full `CalendarEvent`) so callers can construct it from
 * whatever event source they have in scope without dragging the whole type.
 */
export interface SelectedEventEnrichmentArgs {
  /** Agent-facing handle resolved server-side at message-send time. */
  urn: string;
  /** Source ent id (FullCalendar's `gcalId`) — the chip's `/calendar/event/:id` link. */
  eventId: string;
  /** Display label for the chip — the event's summary/title. */
  summary?: string;
  /** ISO start. Used for the chip label. */
  startDateTime?: string;
  /** ISO end. Combined with `startDateTime` for the chip's time range label. */
  endDateTime?: string;
}

const MONTH_ABBREV = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const pad2 = (n: number) => String(n).padStart(2, "0");

/** "6pm" or "5:30pm" — matches `formatChipLabel` in time-range-selection. */
function formatShortTime(zdt: Temporal.ZonedDateTime): string {
  const h = zdt.hour % 12 || 12;
  const m = zdt.minute;
  const meridiem = zdt.hour >= 12 ? "pm" : "am";
  return m === 0 ? `${h}${meridiem}` : `${h}:${pad2(m)}${meridiem}`;
}

function formatMonthDay(zdt: Temporal.ZonedDateTime): string {
  return `${MONTH_ABBREV[zdt.month - 1]} ${zdt.day}`;
}

/**
 * Chip label: `"Standup, Apr 30 9am–10am"` (same-day) or
 * `"Standup, Apr 30 9am – May 1 10am"` (cross-day). Falls back to just the
 * title when start/end aren't both available. Mirrors `formatChipLabel`'s
 * shape from time-range-selection so event chips and range chips read the
 * same in a thread bubble.
 */
function buildChipLabel(args: SelectedEventEnrichmentArgs, timezone: string): string {
  const title = (args.summary ?? "Event").trim() || "Event";
  if (!args.startDateTime || !args.endDateTime) return title;
  try {
    const start = Temporal.Instant.from(args.startDateTime).toZonedDateTimeISO(timezone);
    const end = Temporal.Instant.from(args.endDateTime).toZonedDateTimeISO(timezone);
    const sameDay = start.year === end.year && start.month === end.month && start.day === end.day;
    const timePart = sameDay
      ? `${formatMonthDay(start)} ${formatShortTime(start)}–${formatShortTime(end)}`
      : `${formatMonthDay(start)} ${formatShortTime(start)} – ${formatMonthDay(end)} ${formatShortTime(end)}`;
    return `${title}, ${timePart}`;
  } catch {
    return title;
  }
}

/**
 * Build the user-visible bubble content: the user's prompt, followed by a
 * markdown link chip pointing at the event. Mirrors `buildTimeRangeMessage`
 * — the chip is the rich, clickable handle to the event in the thread.
 */
function buildSelectedEventMessage(
  text: string,
  args: SelectedEventEnrichmentArgs,
  timezone: string,
): string {
  // The event's canonical path. It carries no day of its own because presenting
  // an event already moves the calendar to its day, so the chip
  // doesn't have to encode one.
  return `${text}\n\nEvent: [${buildChipLabel(args, timezone)}](/calendar/event/${args.eventId})`;
}

/**
 * Register a `selectedEvent` calendar-context enrichment for the duration
 * of an event-click popover. The server resolves the URN at message-send
 * time and renders the full event (title, time, location, participants)
 * into the actor's `agentContent` — fresher than a client snapshot and
 * structured so the agent can act on the event handle directly.
 *
 * The submitted message text is also rewritten to append a clickable
 * `[Title, MonDay start–end](/calendar/event/:id)` chip below the user's
 * prompt, matching the time-range selection pattern. The chip renders as a
 * styled link inside the user's message bubble.
 *
 * Pass `null` (or `undefined`) when no event is selected; the hook
 * unregisters cleanly and the next message sends without the event
 * context.
 */
export function useSelectedEventEnrichment(args: SelectedEventEnrichmentArgs | null | undefined) {
  const { register, unregister } = useOmniboxEnrichment();
  const { getUserTimezone } = useCurrentUserContext();

  // Read the props the effect actually depends on so the effect doesn't
  // re-fire when the parent's `args` object identity changes between renders.
  const urn = args?.urn;
  const eventId = args?.eventId;
  const summary = args?.summary;
  const startDateTime = args?.startDateTime;
  const endDateTime = args?.endDateTime;

  useEffect(() => {
    if (!urn || !eventId) {
      unregister(ENRICHMENT_ID);
      return;
    }
    const enrichmentArgs: SelectedEventEnrichmentArgs = {
      urn,
      eventId,
      summary,
      startDateTime,
      endDateTime,
    };
    register({
      id: ENRICHMENT_ID,
      // The popover renders the event details itself — no chip needed in
      // the input. AgentPromptInput doesn't render enrichment chips
      // anyway, but the field is required by the contract.
      renderContent: () => null,
      buildMessage: (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) return undefined;
        const timezone = getUserTimezone();
        const content = buildSelectedEventMessage(trimmed, enrichmentArgs, timezone);
        return {
          text: content,
          calendarContext: {
            kind: "selectedEvent" as const,
            instruction: trimmed,
            timezone,
            urn,
          },
        };
      },
    });
    return () => unregister(ENRICHMENT_ID);
  }, [urn, eventId, summary, startDateTime, endDateTime, register, unregister, getUserTimezone]);
}
